// RigSentinel Context Engine — Phase RCE-1
//
// CONFIDENCE LEVELS
// -----------------
// Every value emitted by the resolver carries a confidence label so the UI
// can render the right indicator (e.g. exact = solid amber, inherited =
// hollow amber, derived = sky-blue, conflict = red, override = magenta,
// unknown = dim grey).
//
//   * exact      — the value was entered by a human into THIS document.
//   * inherited  — the value flowed in from a parent context (Engineering
//                  Session / Well Profile / Rig Sheet).
//   * derived    — the value was computed from other inputs (e.g. casing
//                  shoe depth from the deepest casing row).
//   * conflict   — multiple sources disagree. The UI must prompt the user
//                  to pick the truth.
//   * override   — the user explicitly overrode the resolved value for a
//                  specific document (override scope kept in lib/rce/overrides.js).
//   * unknown    — no source knows the value yet (engine still emits "").
//
// Pure constants + small helpers — no I/O.

export const CONFIDENCE_LEVELS = Object.freeze([
  "exact",
  "inherited",
  "derived",
  "conflict",
  "override",
  "unknown",
]);

// Numeric rank — used by resolver to pick the "best" non-conflicting
// candidate when multiple sources publish the same field. Lower rank wins.
//
//   override  (1) — user explicitly chose a document-scope value
//   exact     (2) — entered into THIS document
//   inherited (3) — flowed in from parent context
//   derived   (4) — computed from other inputs
//   conflict  (5) — sentinel: caller must reconcile
//   unknown   (6) — no source published yet
export const CONFIDENCE_RANK = Object.freeze({
  override:  1,
  exact:     2,
  inherited: 3,
  derived:   4,
  conflict:  5,
  unknown:   6,
});

export function isConfidence(v) {
  return CONFIDENCE_LEVELS.includes(v);
}

/** Numerically compare two confidence levels. Lower rank = stronger value. */
export function compareConfidence(a, b) {
  const ra = CONFIDENCE_RANK[a] ?? 99;
  const rb = CONFIDENCE_RANK[b] ?? 99;
  return ra - rb;
}

/** Pick the stronger of two confidence levels (lowest rank wins). */
export function strongerConfidence(a, b) {
  return compareConfidence(a, b) <= 0 ? a : b;
}
